/**
 * Practice-tunable ROI assumptions — shared shape, defaults, and bounds.
 *
 * Lives in `shared/` because both sides enforce the same contract: the
 * Analytics page's assumption inputs clamp to these bounds client-side, and
 * the `updateRoiAssumptions` callable re-validates against the same table
 * before persisting to the org doc (`roiAssumptions` field). Estimated
 * dollars on the page are always measured counts × these values, badged
 * "Est." — the counts themselves never come from here.
 *
 * Keep the defaults conservative: an ROI page that overstates gets one
 * skeptical question in a renewal call and loses all credibility.
 */

export interface RoiAssumptions {
  /** Loaded hourly cost of front-desk staff. BLS medical receptionist mean
   *  wage is ~$19–22/hr; $22 approximates wage without benefits load. */
  frontDeskHourlyUsd: number
  /** Share (0–1) of patient responsibility that never collects once the
   *  patient leaves without paying (point-of-service collects ~95%+;
   *  post-visit statement differential runs 30–50% industry-wide; 25%
   *  stays conservative). */
  postVisitLeakageRate: number
  /** Revenue written off when a vision visit is served on benefits that
   *  turn out unavailable (exam + materials chair cost). */
  vspWriteOffUsd: number
  /** Reimbursement written off when a medical claim denies unfixably
   *  (out-of-network exam billed blind). */
  medicalWriteOffUsd: number
  /** Staff minutes to rework one fixable denial after the fact. */
  denialReworkMinutes: number
  /** Per-check ceiling on identified patient responsibility — the most one
   *  visit can contribute to "under-collection recovered". A deductible-plan
   *  patient's stored amount is their full remaining deductible (thousands),
   *  but what the desk can actually collect that day is one visit's worth.
   *  Default $150 = our fleet-wide measured copay ceiling and roughly a
   *  comprehensive exam's Medicare-allowed amount. */
  avgVisitResponsibilityUsd: number
}

export const DEFAULT_ASSUMPTIONS: RoiAssumptions = {
  frontDeskHourlyUsd: 22,
  postVisitLeakageRate: 0.25,
  vspWriteOffUsd: 130,
  medicalWriteOffUsd: 130,
  denialReworkMinutes: 25,
  avgVisitResponsibilityUsd: 150,
}

/** Sanity bounds for saved assumptions — shared by the client inputs and the
 *  updateRoiAssumptions callable so a typo can't persist a $2,200/hr desk. */
export const ASSUMPTION_BOUNDS: Record<keyof RoiAssumptions, { min: number; max: number }> = {
  frontDeskHourlyUsd: { min: 5, max: 200 },
  postVisitLeakageRate: { min: 0, max: 1 },
  vspWriteOffUsd: { min: 0, max: 2000 },
  medicalWriteOffUsd: { min: 0, max: 2000 },
  denialReworkMinutes: { min: 0, max: 240 },
  // min matches CAPPABLE_FLOOR_USD (roiAnalytics.service): amounts at or below
  // the floor are returned pre-summed, so an in-bounds cap can never cut them.
  avgVisitResponsibilityUsd: { min: 50, max: 500 },
}

export const ASSUMPTION_KEYS = Object.keys(ASSUMPTION_BOUNDS) as Array<keyof RoiAssumptions>

/** True when `v` is a usable stored value for `key`. */
export function isValidAssumption(key: keyof RoiAssumptions, v: unknown): v is number {
  const { min, max } = ASSUMPTION_BOUNDS[key]
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max
}

/** Merge a stored partial (org doc `roiAssumptions`) over the defaults,
 *  dropping out-of-bounds or non-numeric values rather than trusting them. */
export function resolveAssumptions(stored: unknown): RoiAssumptions {
  const out = { ...DEFAULT_ASSUMPTIONS }
  if (!stored || typeof stored !== 'object') return out
  const rec = stored as Record<string, unknown>
  for (const key of ASSUMPTION_KEYS) {
    const v = rec[key]
    if (isValidAssumption(key, v)) out[key] = v
  }
  return out
}
